/**
 * Utilitário para monitorar e debugar notificações excessivas
 * 
 * Este módulo fornece ferramentas para:
 * - Monitorar a frequência de notificações
 * - Detectar duplicações
 * - Gerar relatórios de uso
 * - Configurar alertas para spam
 */

class NotificationDebugger {
  constructor() {
    this.notifications = [];
    this.duplicates = [];
    this.spamThreshold = 5; // 5 notificações idênticas em 10 segundos
    this.timeWindow = 10000; // 10 segundos
    this.enabled = process.env.NODE_ENV === 'development';
    
    // Interceptar console.log para capturar notificações bloqueadas
    this.interceptConsole();
  }

  // Interceptar logs do NotificationManager
  interceptConsole() {
    if (!this.enabled) return;

    const originalLog = console.log;
    console.log = (...args) => {
      const message = args.join(' ');
      
      // Capturar notificações bloqueadas
      if (message.includes('🚫 Notificação duplicada bloqueada:')) {
        this.recordBlocked(message);
      }
      
      // Capturar notificações de sessão bloqueadas
      if (message.includes('🚫 Notificação de senha restaurada já exibida')) {
        this.recordSessionBlocked(message);
      }
      
      originalLog.apply(console, args);
    };
  }

  // Registrar notificação enviada
  recordNotification(type, message, source = 'unknown') {
    if (!this.enabled) return;

    const notification = {
      id: Date.now() + Math.random(),
      type,
      message,
      source,
      timestamp: new Date(),
      blocked: false
    };

    this.notifications.push(notification);
    this.checkForSpam(type, message);
    this.cleanup();
  }

  // Registrar notificação bloqueada
  recordBlocked(logMessage) {
    const notification = {
      id: Date.now() + Math.random(),
      type: 'blocked',
      message: logMessage,
      source: 'NotificationManager',
      timestamp: new Date(),
      blocked: true
    };

    this.notifications.push(notification);
    this.duplicates.push(notification);
  }

  // Registrar notificação de sessão bloqueada
  recordSessionBlocked(logMessage) {
    const notification = {
      id: Date.now() + Math.random(),
      type: 'session-blocked',
      message: logMessage,
      source: 'NotificationManager',
      timestamp: new Date(),
      blocked: true
    };

    this.notifications.push(notification);
    this.duplicates.push(notification);
  }

  // Verificar spam de notificações
  checkForSpam(type, message) {
    const now = Date.now();
    const recent = this.notifications.filter(n => 
      n.type === type && 
      n.message === message && 
      (now - n.timestamp.getTime()) < this.timeWindow
    );

    if (recent.length >= this.spamThreshold) {
      console.warn(`🚨 SPAM DETECTADO: ${recent.length} notificações "${type}" em ${this.timeWindow/1000}s`);
      console.warn('Mensagem:', message);
      console.warn('Fontes:', [...new Set(recent.map(n => n.source))]);
    }
  }

  // Limpar notificações antigas
  cleanup() {
    const cutoff = Date.now() - (this.timeWindow * 6); // Manter 1 minuto de histórico
    this.notifications = this.notifications.filter(n => 
      n.timestamp.getTime() > cutoff
    );
    this.duplicates = this.duplicates.filter(n => 
      n.timestamp.getTime() > cutoff
    );
  }

  // Gerar relatório de notificações
  generateReport() {
    if (!this.enabled) {
      return { error: 'Debugger desabilitado em produção' };
    }

    const now = new Date();
    const last5Minutes = this.notifications.filter(n => 
      (now.getTime() - n.timestamp.getTime()) < 300000
    );

    // Agrupar por tipo
    const byType = {};
    last5Minutes.forEach(n => {
      if (!byType[n.type]) byType[n.type] = [];
      byType[n.type].push(n);
    });

    // Agrupar por fonte
    const bySource = {};
    last5Minutes.forEach(n => {
      if (!bySource[n.source]) bySource[n.source] = [];
      bySource[n.source].push(n);
    });

    // Detectar possíveis duplicações
    const possibleDuplicates = [];
    Object.keys(byType).forEach(type => {
      const messages = {};
      byType[type].forEach(n => {
        if (!messages[n.message]) messages[n.message] = [];
        messages[n.message].push(n);
      });

      Object.keys(messages).forEach(message => {
        if (messages[message].length > 1) {
          possibleDuplicates.push({
            type,
            message,
            count: messages[message].length,
            sources: [...new Set(messages[message].map(n => n.source))]
          });
        }
      });
    });

    return {
      summary: {
        total: last5Minutes.length,
        blocked: this.duplicates.filter(n => 
          (now.getTime() - n.timestamp.getTime()) < 300000
        ).length,
        timeRange: '5 minutos',
        timestamp: now.toISOString()
      },
      byType: Object.keys(byType).map(type => ({
        type,
        count: byType[type].length,
        sources: [...new Set(byType[type].map(n => n.source))]
      })),
      bySource: Object.keys(bySource).map(source => ({
        source,
        count: bySource[source].length,
        types: [...new Set(bySource[source].map(n => n.type))]
      })),
      possibleDuplicates,
      recommendations: this.generateRecommendations(possibleDuplicates, bySource)
    };
  }

  // Gerar recomendações baseadas no relatório
  generateRecommendations(duplicates, bySource) {
    const recommendations = [];

    // Verificar duplicações
    if (duplicates.length > 0) {
      recommendations.push({
        priority: 'HIGH',
        issue: 'Notificações duplicadas detectadas',
        details: `${duplicates.length} tipos de notificação com duplicatas`,
        solution: 'Centralizar notificações no NotificationManager'
      });
    }

    // Verificar múltiplas fontes para mesmo tipo
    duplicates.forEach(dup => {
      if (dup.sources.length > 1) {
        recommendations.push({
          priority: 'MEDIUM',
          issue: `Múltiplas fontes para "${dup.type}"`,
          details: `Fontes: ${dup.sources.join(', ')}`,
          solution: 'Consolidar em uma única fonte'
        });
      }
    });

    // Verificar volume excessivo
    Object.keys(bySource).forEach(source => {
      if (bySource[source].count > 10) {
        recommendations.push({
          priority: 'MEDIUM',
          issue: `Volume alto de notificações de "${source}"`,
          details: `${bySource[source].count} notificações em 5 minutos`,
          solution: 'Implementar rate limiting ou reduzir frequência'
        });
      }
    });

    return recommendations;
  }

  // Métodos públicos para desenvolvimento
  getStats() {
    return this.generateReport();
  }

  clearHistory() {
    this.notifications = [];
    this.duplicates = [];
    console.log('🧹 Histórico de notificações limpo');
  }

  setSpamThreshold(threshold, timeWindow = 10000) {
    this.spamThreshold = threshold;
    this.timeWindow = timeWindow;
    console.log(`🔧 Spam threshold: ${threshold} notificações em ${timeWindow/1000}s`);
  }

  // Método para ser chamado por NotificationManager
  trackNotification(type, message, source = 'NotificationManager') {
    this.recordNotification(type, message, source);
  }
}

// Instância singleton
const notificationDebugger = new NotificationDebugger();

// Adicionar ao window para debug no console
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  window.notificationDebugger = notificationDebugger;
  console.log('🔍 NotificationDebugger disponível em window.notificationDebugger');
  console.log('Comandos disponíveis:');
  console.log('- notificationDebugger.getStats() - Relatório de notificações');
  console.log('- notificationDebugger.clearHistory() - Limpar histórico');
  console.log('- notificationDebugger.setSpamThreshold(5, 10000) - Configurar spam detection');
}

export default notificationDebugger; 